import {useState} from 'react';

import {browserHistory} from 'react-router';

import {useGeneratePolicyContent} from 'app/policies/api';
import {useGenerateProcedureContent} from 'app/procedures/api';
import {getPathFromURL} from 'app/utils/urls';

import {SuggestionContentType, SuggestionItem} from './types';

import type {FormErrorsProps} from 'app/general/forms';
import type {CreatableDocType} from 'app/general/types';
import type {UserJobSerializedType} from 'sweet-types';

interface Props
  extends Partial<Pick<FormErrorsProps, 'captureFormErrorsFromRejection'>> {
  createPromise: Promise<CreatableDocType>;
  generateContent:
    | ReturnType<typeof useGeneratePolicyContent>
    | ReturnType<typeof useGenerateProcedureContent>;
  onSuccess: (job: UserJobSerializedType, element: unknown) => unknown;
  onError?: (url: string) => unknown;
}

export function generateNewDocumentContent({
  createPromise,
  generateContent,
  onSuccess,
  onError,
  captureFormErrorsFromRejection,
}: Props) {
  function goToEditUrl(url) {
    browserHistory.push(`${getPathFromURL(url)}edit/`);
  }

  return createPromise.then(
    (element) =>
      generateContent[0]({
        url: element.current_version.url,
        prompt: element.name,
      })
        .unwrap()
        .then(
          (jobResults: UserJobSerializedType) => onSuccess(jobResults, element),
          onError != null
            ? onError
            : () => goToEditUrl(element.current_version.html_url)
        ),
    (err) => {
      if (captureFormErrorsFromRejection != null) {
        captureFormErrorsFromRejection(err);
      }
      throw err;
    }
  );
}

export function extractSuggestionsOfType(
  suggestions: Array<SuggestionItem>,
  contentType: SuggestionContentType
) {
  return suggestions
    .filter((item) => item.content_type === contentType)
    .map((item) => item.name);
}

export const cleanStepId = (stepId?: string): string =>
  (stepId ?? '').replace(/stepid-/, '');

export function useHistoryData<T extends {source: string}>(
  initialValue?: T
): [Array<T>, (newEntry: T) => void, (entry: T) => void] {
  const [history, setHistory] = useState<Array<T>>([]);

  const appendHistory = (newEntry: T) => {
    setHistory((prevHistory) => [newEntry, ...prevHistory]);
  };

  const setCurrentMessage = (entry: T) => {
    setHistory((prevHistory) => {
      const newHistory = [...prevHistory];
      if (newHistory.length > 0 && newHistory[0].source === entry.source) {
        newHistory[0] = entry;
      } else {
        newHistory.unshift(entry); // In case the history is empty
      }
      return newHistory;
    });
  };

  if (initialValue != null && history.length === 0) {
    appendHistory(initialValue);
  }

  return [history, appendHistory, setCurrentMessage];
}

export const AI_POLICY_PROMPT = `
Write the content of this policy.

The content should delve into extensive detail across each section, meticulously
delineating the procedures, defining essential terms, and providing illustrative
examples, including what to do and what to avoid. Start the content with distinct
headings for 'Scope' and 'Audience' to ensure clarity and focus. Whenever referring to
the Company Name within the text ensure it is surrounded by <strong></strong> tags to maintain
consistency and emphasis.

Aim for precision in your instructions, weaving them into the narrative with clear
step-by-step guidance. Employ simple HTML to enhance the document's readability and
structure.

Thank you
`;

export const AI_PROCEDURE_PROMPT = `
Please create a minimum viable procedure for our company. This should include the following:

Name: A clear and concise name for the procedure
Description: A detailed description of the procedure, employing simple HTML markup to enhance
  readability and structure. Include headings, paragraphs, and any necessary formatting elements to make
  the description visually appealing and easy to understand.
  Headings in the description area must be h3 or below.
Steps: Add steps with names to define the procedure outline.
  Do not include any content for these steps. Avoid step numbering in the titles,
  our UI will handle numbering automatically.

The aim is to provide a skeleton for the procedure with precision and clarity.
The detailed description should weave instructions into a narrative with clear, step-by-step guidance.

Thank you
`;
